// src/Utils/GlobalContext.js
import React, { createContext, useState, useEffect, useMemo, useCallback } from "react";

export const GlobalContext = createContext({
  globalData: {},
  setGlobalData: () => {},
  darkMode: false,
  toggleDarkMode: () => {},
});

export const GlobalProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("themePreference") === "dark");
  const [menuType, setMenuTypeState] = useState(() => localStorage.getItem("menuType") || "lateral");

  const setMenuType = useCallback((type) => {
    localStorage.setItem("menuType", type);
    setMenuTypeState(type);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("menu-superior", menuType === "superior");
    if (menuType === "superior") document.body.classList.remove("sidebar-collapsed");
  }, [menuType]);

  const [globalData, setGlobalData] = useState(() => {
    let novidadesVersao = [];
    try {
      const raw = localStorage.getItem("novidadesVersao");
      if (raw) novidadesVersao = JSON.parse(raw);
    } catch (_) {}
    const temNovidades = Boolean(
      localStorage.getItem("temNovidades") === "true" && Array.isArray(novidadesVersao) && novidadesVersao.length > 0
    );
    return {
      user_id: localStorage.getItem("user_id") || null,
      entidade_id: localStorage.getItem("entidade_id") || null,
      user: localStorage.getItem("user") || null,
      classificacao: localStorage.getItem("classificacao") || null,
      permissoes_grupo: JSON.parse(localStorage.getItem("permissoes_grupo") || "null"),
      numero_linhas_grid: Number(localStorage.getItem("numero_linhas_grid")) || 10,
      super_admin: localStorage.getItem("super_admin") || "N",
      mensalidade_abonada: localStorage.getItem("mensalidade_abonada") || "N",
      rota_padrao: null,
      pagina_padrao_classificacao: null,
      temNovidades: temNovidades || false,
      novidadesVersao: novidadesVersao || [],
      // Item #34: nunca persistido em localStorage — sempre revalidado no
      // backend a cada bootstrap (login/seleção de entidade).
      situacaoFinanceira: null,
      // Item #36 (LGPD): nunca persistido — cada tela que precisa mascarar uma
      // exportação consulta `empresasConfiguracoes` da entidade ativa e grava
      // aqui. `null`/ausente deve ser tratado como "oculta" pelo consumidor
      // (falha segura), nunca como "mostra".
      lgpdOcultarDados: null,
    };
  });

  const toggleDarkMode = useCallback(() => setDarkMode((prev) => !prev), []);

  useEffect(() => {
    localStorage.removeItem("darkMode");
    localStorage.setItem("themePreference", darkMode ? "dark" : "light");
    const isLoginPage = document.body.classList.contains("login-page-body");
    const applyDark = darkMode && !isLoginPage;
    document.body.classList.toggle("dark-mode", applyDark);
    document.body.setAttribute("data-bs-theme", applyDark ? "dark" : "light");
  }, [darkMode, globalData.user_id]);

  // Persistência user_id
  useEffect(() => {
    if (globalData.user_id) localStorage.setItem("user_id", globalData.user_id);
    else localStorage.removeItem("user_id");
  }, [globalData.user_id]);

  // Persistência user (email)
  useEffect(() => {
    if (globalData.user) localStorage.setItem("user", globalData.user);
    else localStorage.removeItem("user");
  }, [globalData.user]);

  // Persistência entidade_id
  useEffect(() => {
    if (globalData.entidade_id) localStorage.setItem("entidade_id", globalData.entidade_id);
    else localStorage.removeItem("entidade_id");
  }, [globalData.entidade_id]);

  // Persistência permissões
  useEffect(() => {
    if (globalData.permissoes_grupo) {
      localStorage.setItem("permissoes_grupo", JSON.stringify(globalData.permissoes_grupo));
    } else {
      localStorage.removeItem("permissoes_grupo");
    }
  }, [globalData.permissoes_grupo]);

  // 🔹 Persistência classificacao
  useEffect(() => {
    if (globalData.classificacao) localStorage.setItem("classificacao", globalData.classificacao);
    else localStorage.removeItem("classificacao");
  }, [globalData.classificacao]);

  // 🔹 Persistência numero_linhas_grid (config da grid)
  useEffect(() => {
    if (globalData.numero_linhas_grid) {
      localStorage.setItem("numero_linhas_grid", String(globalData.numero_linhas_grid));
    } else {
      localStorage.removeItem("numero_linhas_grid");
    }
  }, [globalData.numero_linhas_grid]);

  // Persistência super_admin
  useEffect(() => {
    if (globalData.super_admin) localStorage.setItem("super_admin", globalData.super_admin);
    else localStorage.removeItem("super_admin");
  }, [globalData.super_admin]);

  // Persistência mensalidade_abonada
  useEffect(() => {
    if (globalData.mensalidade_abonada === "S") localStorage.setItem("mensalidade_abonada", "S");
    else localStorage.removeItem("mensalidade_abonada");
  }, [globalData.mensalidade_abonada]);

  // Item #XXX: Sincronizar mensalidade_abonada entre abas/sessões
  // Se o valor mudar em outra aba, detecta e atualiza o estado local
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "mensalidade_abonada") {
        setGlobalData((prev) => ({
          ...prev,
          mensalidade_abonada: e.newValue === "S" ? "S" : "N",
        }));
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // 🔹 Persistência novidades de versão (não lidas)
  useEffect(() => {
    if (globalData.temNovidades && Array.isArray(globalData.novidadesVersao) && globalData.novidadesVersao.length > 0) {
      localStorage.setItem("temNovidades", "true");
      localStorage.setItem("novidadesVersao", JSON.stringify(globalData.novidadesVersao));
    } else {
      localStorage.removeItem("temNovidades");
      localStorage.removeItem("novidadesVersao");
    }
  }, [globalData.temNovidades, globalData.novidadesVersao]);

  const value = useMemo(
    () => ({ globalData, setGlobalData, darkMode, toggleDarkMode, menuType, setMenuType }),
    [globalData, darkMode, toggleDarkMode, menuType, setMenuType]
  );

  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
};
